import type { SheetData } from 'write-excel-file/browser';

export type MergeMpassStatus =
    | 'moved'
    | 'merged'
    | 'retained'
    | 'none'
    | 'not_transferred'
    | 'unknown';

export const formatMergeMpassStatus = (
    status: MergeMpassStatus | undefined,
    translate: (key: string) => string
) => translate(`merge.history.mpass_${status || 'unknown'}`);

export interface MergeReport {
    school_name: string | null;
    data: Array<{
        created: string;
        primary: { name: string; identifier: string | null };
        source: { name: string; identifier: string | null };
        identifier_after: string | null;
        mpass_status?: MergeMpassStatus;
        released_school_licenses: number | null;
        released_school_licenses_estimated?: boolean;
        released_products_estimated?: boolean;
        released_products: Array<{
            product_name: string | null;
            count: number;
        }> | null;
    }>;
}

export const formatReleasedProducts = (
    products: MergeReport['data'][number]['released_products'],
    translate: (key: string) => string
) => {
    if (products == null) return translate('merge.history.report_not_recorded');
    const names = products
        .map(
            product =>
                product.product_name ?? translate('merge.history.report_unknown')
        )
        .join('\n');
    return names;
};

export const formatReleasedLicenseCount = (
    entry: MergeReport['data'][number],
    translate: (key: string) => string
): number | string => {
    if (entry.released_school_licenses == null)
        return translate('merge.history.report_not_recorded');
    return entry.released_school_licenses;
};

export const getHelsinkiDate = (date = new Date()) =>
    date.toLocaleDateString('sv-SE', { timeZone: 'Europe/Helsinki' });

export const formatMergeCancelDeadline = (deadline: string) =>
    new Date(new Date(deadline).getTime() - 1).toLocaleDateString('fi-FI', {
        timeZone: 'Europe/Helsinki',
        dateStyle: 'short',
    });

export const formatMergeReportTime = (created: string) =>
    new Date(created).toLocaleString('fi-FI', {
        timeZone: 'Europe/Helsinki',
        dateStyle: 'short',
        timeStyle: 'medium',
    });

export const getSchoolReportFilename = (
    prefix: 'tuplat' | 'yhdistamiset',
    school: string | null,
    resource: string,
    dates: string
) => {
    const name = (school || 'oppilaitos')
        .replace(/[^a-zA-Z0-9\u00c0-\u024f-]/g, '_')
        .slice(0, 80);
    return `${prefix}-${name}-${resource}-${dates}.xlsx`;
};

interface DuplicateReportGroup {
    note: string;
    accounts: Array<{
        name: string;
        identifier: string;
        mpass: boolean;
        schoolLicenseCount: number;
        validSchoolLicenseCount: number;
        primary: boolean;
        retainIdentifier: boolean;
    }>;
    overlappingLicenseCount: number;
}

export const getDuplicateReportSheet = (
    groups: DuplicateReportGroup[],
    school: string,
    role: string,
    search: string,
    translate: (key: string) => string
): SheetData => {
    const text = (value: string) => ({ value, type: String });
    return [
        [text(translate('merge.bulk.report_title')), text(school)],
        [text(translate('merge.history.report_role')), text(role)],
        [text(translate('merge.bulk.report_search')), text(search)],
        [text(translate('merge.bulk.report_note'))],
        [],
        [
            'report_group',
            'report_name',
            'account',
            'mpass',
            'report_active_licenses',
            'report_valid_active_licenses',
            'report_overlapping_licenses',
            'report_primary',
            'report_identifier',
            'report_status',
        ].map(key => ({
            ...text(translate(`merge.bulk.${key}`)),
            fontWeight: 'bold' as const,
        })),
        ...groups.flatMap((group, index) =>
            group.accounts.map(account => [
                text(String(index + 1)),
                text(account.name),
                text(account.identifier),
                text(
                    translate(
                        account.mpass ? 'ra.boolean.true' : 'ra.boolean.false'
                    )
                ),
                { value: account.schoolLicenseCount, type: Number },
                { value: account.validSchoolLicenseCount, type: Number },
                { value: group.overlappingLicenseCount, type: Number },
                text(account.primary ? translate('ra.boolean.true') : ''),
                text(
                    account.retainIdentifier ? translate('ra.boolean.true') : ''
                ),
                text(group.note),
            ])
        ).map((row, index) =>
            row.map(cell => ({
                ...cell,
                backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F1F4F8',
            }))
        ),
    ];
};

export const getMergeReportSheet = (
    report: MergeReport,
    from: string,
    until: string,
    role: string,
    translate: (key: string) => string
): SheetData => {
    const text = (value: string | null) => ({
        value: value ?? '',
        type: String,
    });
    return [
        [
            text(translate('merge.history.report_title')),
            text(report.school_name),
        ],
        [text(translate('merge.history.report_role')), text(role)],
        [
            text(translate('merge.history.report_from')),
            text(from),
            text(translate('merge.history.report_until')),
            text(until),
        ],
        [
            text(translate('merge.history.report_note')),
            text(translate('merge.history.mpass_help')),
            text(translate('merge.history.report_missing_audit')),
        ],
        [],
        [
            'created',
            'report_primary_name',
            'report_primary_identifier',
            'report_source_name',
            'report_source_identifier',
            'report_identifier_after',
            'report_released_licenses',
            'report_released_products',
            'mpass_status',
        ].map(key => ({
            ...text(translate(`merge.history.${key}`)),
            fontWeight: 'bold' as const,
        })),
        ...report.data.map((entry, index) => {
            const count = formatReleasedLicenseCount(entry, translate);
            return [
                text(formatMergeReportTime(entry.created)),
                text(entry.primary.name),
                text(entry.primary.identifier),
                text(entry.source.name),
                text(entry.source.identifier),
                text(entry.identifier_after),
                typeof count === 'number'
                    ? { value: count, type: Number }
                    : text(count),
                text(
                    formatReleasedProducts(
                        entry.released_products,
                        translate
                    )
                ),
                text(formatMergeMpassStatus(entry.mpass_status, translate)),
            ].map(cell => ({
                ...cell,
                backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F1F4F8',
            }));
        }),
    ];
};
